import { CheckIcon, ChevronIcon } from "@/components/Icon";
import { codeOf, shortDate } from "@/lib/airtime";
import type { SeasonEpisode, SeasonView } from "@/lib/seasons";
import { seasonState } from "@/lib/seasons";

interface Actions {
  /** The dot: this one episode, nothing else. */
  onToggleEpisode: (episode: SeasonEpisode) => void;
  /** The row: "I got this far", sweeping everything before it. */
  onSetPosition: (episode: SeasonEpisode) => void;
  /** Marks the season and everything before it, or unmarks it if it is done. */
  onMarkSeason: (season: SeasonView) => void;
}

function EpisodeRow({
  episode,
  onToggle,
  onSetPosition,
}: {
  episode: SeasonEpisode;
  onToggle: () => void;
  onSetPosition: () => void;
}) {
  const code = codeOf(episode);
  return (
    <div
      className="ep-row"
      data-w={episode.watched ? "1" : "0"}
      data-air={episode.aired ? "1" : "0"}
    >
      <button
        type="button"
        className="dot"
        onClick={onToggle}
        aria-label={`${episode.watched ? "Unmark" : "Mark"} ${code} only`}
      >
        <CheckIcon />
      </button>
      <button
        type="button"
        className="ep-main"
        onClick={onSetPosition}
        aria-label={
          episode.watched
            ? `Set progress to before ${code}`
            : `Mark everything watched through ${code}`
        }
      >
        <span className="ep-num">{String(episode.episode).padStart(2, "0")}</span>
        <span className="ep-name">{episode.title}</span>
        <span className="ep-d">
          {episode.aired && episode.airsAt !== null ? shortDate(episode.airsAt) : "upcoming"}
        </span>
      </button>
    </div>
  );
}

function Season({
  season,
  open,
  pending,
  onToggleOpen,
  actions,
}: {
  season: SeasonView;
  open: boolean;
  pending: boolean;
  onToggleOpen: () => void;
  actions: Actions;
}) {
  const state = seasonState(season);
  const drawerId = `drawer-${String(season.number)}`;
  return (
    <>
      <div className="season-row">
        <button
          type="button"
          className="season-head"
          aria-expanded={open}
          aria-controls={drawerId}
          onClick={onToggleOpen}
        >
          <span className="chev">
            <ChevronIcon />
          </span>
          <span className="season-n">{season.label}</span>
          <span className="season-c">
            {season.watchedCount}/{season.episodes.length}
          </span>
        </button>
        <button
          type="button"
          className={pending ? "season-mark pending" : "season-mark"}
          data-state={state}
          disabled={season.airedCount === 0}
          aria-label={
            state === "all" ? `Unmark ${season.label}` : `Mark ${season.label} as watched`
          }
          onClick={() => {
            actions.onMarkSeason(season);
          }}
        >
          <span className="season-dot">
            <CheckIcon />
          </span>
        </button>
      </div>
      <div className={open ? "drawer open" : "drawer"} id={drawerId}>
        <div className="drawer-inner">
          {season.episodes.map((episode) => (
            <EpisodeRow
              key={episode.episode}
              episode={episode}
              onToggle={() => {
                actions.onToggleEpisode(episode);
              }}
              onSetPosition={() => {
                actions.onSetPosition(episode);
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
}

export function SeasonList({
  seasons,
  openSeasons,
  pending,
  onToggleSeason,
  actions,
}: {
  seasons: readonly SeasonView[];
  openSeasons: ReadonlySet<number>;
  /** A write for this show is queued and has not reached Simkl yet. */
  pending: boolean;
  onToggleSeason: (number: number) => void;
  actions: Actions;
}) {
  return (
    <>
      {seasons.map((season) => (
        <Season
          key={season.number}
          season={season}
          open={openSeasons.has(season.number)}
          pending={pending}
          onToggleOpen={() => {
            onToggleSeason(season.number);
          }}
          actions={actions}
        />
      ))}
    </>
  );
}
